/**
 * Sheet — the one overlay primitive.
 *
 * The app had 28 overlay implementations, 7 backdrop treatments and 6 close
 * buttons. Ten of the dialogs were hand-rolled `fixed inset-0` divs: none had
 * a focus trap, only one overlay in the entire app locked body scroll, nine
 * had no `role="dialog"`, seven had no Esc handler and four could not be
 * dismissed by clicking outside. Every close affordance was under the 44px
 * minimum. Nothing became a bottom sheet on a phone.
 *
 * All of that is now one component built on Radix Dialog.
 *
 * WHAT RADIX GIVES US (verified against @radix-ui/react-dialog 1.1.15 dist):
 *   - focus trap        FocusScope with `trapped` + `loop`, plus focus guards
 *   - Esc               DismissableLayer -> onEscapeKeyDown -> onDismiss
 *   - click-outside     DismissableLayer -> onPointerDownOutside
 *   - body scroll lock  `RemoveScroll` — note it is rendered by *Overlay*, not
 *                       Content, so SheetContent always renders SheetOverlay.
 *                       Never render DialogPrimitive.Content without it.
 *   - `role="dialog"` + `aria-modal`, aria-labelledby/aria-describedby wiring
 *   - the rest of the page hidden from assistive tech (`aria-hidden`)
 *   - focus returned to the trigger on close
 *
 * WHAT IT DOES NOT GIVE US, and is therefore implemented here:
 *   - responsive presentation (bottom sheet below sm, centred at sm+)
 *   - safe-area padding on the bottom edge
 *   - drag-to-dismiss (see the grabber below)
 *   - exit/enter motion (sheet.css — this app has no tailwindcss-animate)
 *   - a close affordance that meets the 44px touch minimum
 *   - silence: Radix `console.error`s when Content has no Title and
 *     `console.warn`s when `aria-describedby` points at nothing. Always render
 *     a <SheetTitle> (use `className="sr-only"` if it should not be seen);
 *     the describedby attribute is dropped automatically unless you opt in.
 *
 * @example
 *   <Sheet open={open} onOpenChange={setOpen}>
 *     <SheetContent className="max-w-lg">
 *       <div className="flex items-center justify-between p-4">
 *         <SheetTitle className="text-lg font-bold text-cream">Title</SheetTitle>
 *       </div>
 *       …
 *     </SheetContent>
 *   </Sheet>
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import './sheet.css';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetPortal = DialogPrimitive.Portal;
/** Raw Radix Close — use with `asChild` to make an existing button dismiss. */
const SheetClose = DialogPrimitive.Close;

/**
 * `center` — bottom sheet below `sm`, centred dialog at `sm`+. The default,
 *            and what a dialog should be.
 * `bottom`  — bottom-anchored at every width, for things that are sheets by
 *             nature (a picks summary, a filter tray) rather than dialogs.
 */
export type SheetSide = 'center' | 'bottom';

/* -------------------------------------------------------------------------- */
/* Overlay — the single backdrop treatment in the app                          */
/* -------------------------------------------------------------------------- */

/**
 * The one backdrop treatment. It replaces seven: a raw-hex surface at 90%
 * with blur, `bg-surface/80`+blur, `bg-surface/75`, `bg-surface/50`,
 * `bg-black/70`, `bg-black/60`+blur and `bg-black/60`. Token-based — the old
 * primitive's was an arbitrary literal that could not follow a theme change
 * even though it lived in the one shared component.
 *
 * Exported so sheet.test.ts can pin it; there should be exactly one of these.
 */
export const SHEET_OVERLAY =
  'fp-sheet-overlay fixed inset-0 z-modal bg-surface/85 backdrop-blur-sm';

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(SHEET_OVERLAY, className)}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

/* -------------------------------------------------------------------------- */
/* Close — the single close affordance, 44px                                   */
/* -------------------------------------------------------------------------- */

/**
 * `p-2.5` around an `h-5 w-5` glyph is 40px; `min-h-11 min-w-11` takes the
 * real hit target to the 44px minimum without changing where the icon sits.
 * Place it inline in a header, or let SheetContent position it for you with
 * `showClose`.
 *
 * This replaces six close-button styles that ranged 16-36px — including the
 * old shared primitive's own ~24px, which is how a design system ends up
 * shipping the bug everywhere at once.
 */
export const SHEET_CLOSE = [
  'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-control p-2.5',
  'text-tertiary transition-colors hover:bg-surface-3 hover:text-cream',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2',
  'disabled:pointer-events-none disabled:opacity-50',
].join(' ');

const SheetCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    aria-label="Close"
    className={cn(SHEET_CLOSE, className)}
    {...props}
  >
    <X className="h-5 w-5" aria-hidden="true" />
  </DialogPrimitive.Close>
));
SheetCloseButton.displayName = 'SheetCloseButton';

/* -------------------------------------------------------------------------- */
/* Content                                                                     */
/* -------------------------------------------------------------------------- */

// The overlay weight lives in --shadow-overlay, not in a `shadow-black/50`
// next to it: a custom --shadow-* token bakes its own colour in, so the colour
// utility sets --tw-shadow-color and nothing ever reads it. See globals.css.
export const SHEET_PANEL_BASE =
  'fp-sheet-panel fixed z-modal flex flex-col overflow-y-auto border border-surface-3 bg-surface-2 shadow-overlay outline-none';

/**
 * Below `sm` both sides are the same bottom sheet: full-bleed, rounded top,
 * safe-area padded.
 *
 * `max-w-form` and `max-h-[90dvh]` are declared in the *base* variant on
 * purpose. Thirteen existing dialogs override the width with a plain
 * `max-w-lg` / `max-w-5xl`; if the default lived in a `sm:` variant it would
 * out-cascade every one of those overrides at desktop. Base defaults let
 * tailwind-merge do its job. `max-sm:max-w-none` then restores full-bleed
 * below `sm`, and the two variants partition the range exactly.
 */
const SHEET_PANEL_MOBILE =
  'inset-x-0 bottom-0 mx-auto w-full max-w-form max-sm:max-w-none max-h-[90dvh] rounded-t-panel border-b-0 pb-[env(safe-area-inset-bottom)]';

export const SHEET_PANEL_SIDE: Record<SheetSide, string> = {
  // Centred dialog at sm+. `right-auto`/`bottom-auto` release the mobile edge
  // anchoring before the centring lands.
  center: [
    'fp-sheet-panel--center',
    SHEET_PANEL_MOBILE,
    'sm:bottom-auto sm:right-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100%_-_2rem)]',
    'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-panel sm:border-b sm:pb-0',
  ].join(' '),
  // Bottom-anchored everywhere; `mx-auto` centres it once max-width bites.
  bottom: ['fp-sheet-panel--bottom', SHEET_PANEL_MOBILE].join(' '),
};

/** Past this many px of downward drag, release dismisses the sheet. */
const DRAG_DISMISS_PX = 96;

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: SheetSide;
  /** Absolutely-positioned 44px close button in the top-right. */
  showClose?: boolean;
  /** The mobile drag handle. Hidden at `sm`+ where the panel is a dialog. */
  showGrabber?: boolean;
  /** Dragging the grabber down past ~96px closes the sheet. */
  dragToDismiss?: boolean;
  /**
   * `false` blocks Esc and outside-click. For confirms that must be answered,
   * and for "don't let them close mid-submit" — pass `dismissible={!submitting}`.
   */
  dismissible?: boolean;
  /**
   * Set this when the sheet renders a <SheetDescription>, so Radix wires
   * `aria-describedby` to it. Left off, the attribute is dropped — Radix
   * points it at an id that only exists when a Description is rendered and
   * warns to the console when that id dangles.
   */
  hasDescription?: boolean;
  /** Escape hatch for a per-dialog backdrop tweak. There should be no need. */
  overlayClassName?: string;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  {
    className,
    children,
    side = 'center',
    showClose = true,
    showGrabber = true,
    dragToDismiss = true,
    dismissible = true,
    hasDescription = false,
    overlayClassName,
    onEscapeKeyDown,
    onInteractOutside,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref
) {
  // Radix exposes no imperative close, so drag-dismiss clicks a hidden Close.
  // It is inert to both pointer and assistive tech.
  const dismissRef = React.useRef<HTMLButtonElement>(null);
  const dragOriginRef = React.useRef<{ y: number; pointerId: number } | null>(null);
  const [dragY, setDragY] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [snapping, setSnapping] = React.useState(false);

  //  has to close the drag route too, or a confirm that
  // must be answered could still be flicked away.
  const canDrag = dragToDismiss && showGrabber && dismissible;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canDrag) return;
    dragOriginRef.current = { y: event.clientY, pointerId: event.pointerId };
    setDragging(true);
    setSnapping(false);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current;
    if (!origin || origin.pointerId !== event.pointerId) return;
    // Downward only — an upward drag on a sheet means nothing.
    setDragY(Math.max(0, event.clientY - origin.y));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current;
    if (!origin || origin.pointerId !== event.pointerId) return;
    const travelled = Math.max(0, event.clientY - origin.y);
    dragOriginRef.current = null;
    setDragging(false);
    setDragY(0);
    if (travelled > DRAG_DISMISS_PX) {
      // Let the exit keyframe carry it the rest of the way down. CSS
      // animations outrank inline styles in the cascade, so it plays cleanly.
      dismissRef.current?.click();
    } else if (travelled > 0) {
      setSnapping(true);
    }
  };

  // Radix wires `aria-describedby` to an id that only exists when a
  // <SheetDescription> is rendered, and warns when it dangles. Opt in with
  // `hasDescription`; otherwise the attribute is dropped (or replaced by an
  // explicit id the caller supplied).
  const describedBy = hasDescription ? {} : { 'aria-describedby': ariaDescribedBy };

  return (
    <SheetPortal>
      {/* Always rendered: Radix hangs RemoveScroll (the body scroll lock) off
          the Overlay, not off Content. */}
      <SheetOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(SHEET_PANEL_BASE, SHEET_PANEL_SIDE[side], className)}
        // Inline transform only while the user is actually dragging (mobile
        // only — the grabber is `sm:hidden`, so this can never collide with
        // the `sm:-translate-*` centring).
        style={
          dragY > 0
            ? { transform: `translate3d(0, ${dragY}px, 0)`, transition: 'none' }
            : dragging
              ? { transition: 'none' }
              : snapping
                ? {
                    transform: 'translate3d(0, 0, 0)',
                    transition: 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)',
                  }
                : undefined
        }
        onTransitionEnd={() => setSnapping(false)}
        onEscapeKeyDown={(event) => {
          if (!dismissible) event.preventDefault();
          onEscapeKeyDown?.(event);
        }}
        onInteractOutside={(event) => {
          if (!dismissible) event.preventDefault();
          onInteractOutside?.(event);
        }}
        {...props}
        {...describedBy}
      >
        {showGrabber && (
          <div
            // `touch-none` stops the browser claiming the gesture as a scroll.
            className="flex shrink-0 touch-none justify-center pt-2 pb-1 sm:hidden"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            aria-hidden="true"
          >
            <span className="h-1.5 w-10 rounded-pill bg-surface-4" />
          </div>
        )}

        {children}

        {showClose && <SheetCloseButton className="absolute right-2 top-2" />}

        {canDrag && (
          <DialogPrimitive.Close
            ref={dismissRef}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          />
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = DialogPrimitive.Content.displayName;

/* -------------------------------------------------------------------------- */
/* Title / Description                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Deliberately unstyled. Type is owned by the scale in globals.css, not by the
 * overlay chassis — pass the classes the heading should have. Required on
 * every sheet for screen readers; use `className="sr-only"` to hide it.
 */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn(className)} {...props} />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

/** Renders a <p>. Pass `aria-describedby={id}` on SheetContent to link it. */
const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn(className)} {...props} />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetClose,
  SheetCloseButton,
  SheetTitle,
  SheetDescription,
};
